// app.dbAdmin — the ADMIN inspector over this project's database: the tree of
// what's actually stored (via @s3p/sdk/db-inspect) plus raw byte-level
// read/write/delete on classified keys. This is the engine behind the
// console's Database panel, exposed so any builder can ship the same "open the
// hood" screen.
//
// Distinct from `app.collection`/`app.doc` on purpose: those are the typed,
// happy-path stores. This module operates on RAW keys — it shows encryption
// envelopes, binary payloads and system objects the typed layer hides. IAM
// still gates everything (a member sees exactly what their role can list).

import {
  DeleteObjectCommand,
  GetObjectCommand,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import {
  type AccessAnalysis,
  type DatabaseTree,
  analyzeAccess,
  type ClassifiedEntry,
  groupEntries,
  loadDatabaseTree,
  scanEntries,
} from "./db-inspect.ts";
import { APP_DATA_SCAN_PREFIXES, paths } from "./paths.ts";

export type { AccessAnalysis, ClassifiedEntry, DatabaseTree, ScanNote } from "./db-inspect.ts";

export interface DbAdminModule {
  /** Scan the database and return the grouped, classified tree — collections,
   *  logs, docs, system objects. `sampleSize` caps the entries kept per group
   *  (default 50).
   *
   *  With NO `scanPrefixes` it scans one root PER SCOPE (`s3db/shared/`,
   *  `s3db/admin/`, `s3db/private/`), because `s3:ListBucket` is granted with a
   *  prefix condition that no role's policy matches against a bare `s3db/`.
   *  Roots the caller can't list come back in `tree.notes` rather than
   *  throwing, and a denied `s3db/private/` is retried narrowed to the caller's
   *  own space. Passing `scanPrefixes` opts out of all of that and scans
   *  exactly what you name. */
  tree(opts?: {
    scanPrefixes?: string[];
    maxKeys?: number;
    sampleSize?: number;
  }): Promise<DatabaseTree>;
  /** Raw bytes of one object. */
  read(rawKey: string): Promise<Uint8Array>;
  /** Overwrite one object with raw text/bytes. No schema, no index update —
   *  this is the surgical write, not `collection().put()`. */
  write(
    rawKey: string,
    body: string | Uint8Array,
    opts?: { contentType?: string },
  ): Promise<void>;
  /** Delete one object by raw key. */
  remove(rawKey: string): Promise<void>;
  /** Both enforcement layers for one entry, in plain English: path shape,
   *  read/write IAM hints, and the crypto envelope's recipients (pass the
   *  object's bytes to include them). */
  analyze(entry: ClassifiedEntry, bytes?: Uint8Array): AccessAnalysis;
}

export interface DbAdminConfig {
  bucket: string;
  bucketPrefix: string;
  slug: string;
  getClient: () => Promise<S3Client>;
  /** The signed-in user's sub, when known. Used only to narrow a denied
   *  `s3db/private/` scan to the caller's OWN private space, which the member
   *  floor always allows. */
  sub?: () => string | undefined;
}

export function createDbAdmin(cfg: DbAdminConfig): DbAdminModule {
  return {
    async tree(opts = {}) {
      const s3 = await cfg.getClient();
      if (opts.scanPrefixes) {
        return loadDatabaseTree(s3, cfg.bucket, cfg.bucketPrefix, {
          scanPrefixes: opts.scanPrefixes,
          ...(opts.maxKeys ? { maxKeys: opts.maxKeys } : {}),
          ...(opts.sampleSize ? { sampleSize: opts.sampleSize } : {}),
        });
      }
      const scanOpts = opts.maxKeys ? { maxKeys: opts.maxKeys } : {};
      const roots = APP_DATA_SCAN_PREFIXES.map((p) => `${cfg.bucketPrefix}${p}`);
      const scan = await scanEntries(s3, cfg.bucket, cfg.bucketPrefix, roots, scanOpts);

      // Listing ALL of `s3db/private/` needs read-all (owner). Everyone else is
      // denied there — but the member floor still lists their OWN private
      // prefix, and showing an admin their own data beats showing them a
      // denial. Retry narrowed, and say so: "your own only" and "all users"
      // must never look the same in an admin browser.
      const privateRoot = `${cfg.bucketPrefix}${paths().dbRoot()}private/`;
      const denied = scan.notes.find((n) => n.prefix === privateRoot && n.status === "denied");
      const sub = cfg.sub?.();
      if (denied && sub) {
        const own = `${privateRoot}${sub}/`;
        try {
          const mine = await scanEntries(s3, cfg.bucket, cfg.bucketPrefix, [own], scanOpts);
          scan.entries.push(...mine.entries);
          scan.totalCount += mine.totalCount;
          scan.truncated = scan.truncated || mine.truncated;
          denied.status = "narrowed";
          denied.readPrefix = own;
          denied.message =
            "only your own private space is listable — reading every user's private data needs the read-all capability (owner)";
        } catch {
          // Still denied: leave the original note exactly as it is.
        }
      }

      return {
        bucketPrefix: cfg.bucketPrefix,
        totalCount: scan.totalCount,
        groups: groupEntries(scan.entries, {
          ...(opts.sampleSize ? { sampleSize: opts.sampleSize } : {}),
        }),
        truncated: scan.truncated,
        notes: scan.notes,
      };
    },
    async read(rawKey) {
      const s3 = await cfg.getClient();
      const res = await s3.send(new GetObjectCommand({ Bucket: cfg.bucket, Key: rawKey }));
      const body = res.Body as { transformToByteArray: () => Promise<Uint8Array> } | undefined;
      if (!body) throw new Error(`app.dbAdmin.read: empty body for '${rawKey}'`);
      return body.transformToByteArray();
    },
    async write(rawKey, body, opts = {}) {
      const s3 = await cfg.getClient();
      await s3.send(
        new PutObjectCommand({
          Bucket: cfg.bucket,
          Key: rawKey,
          Body: typeof body === "string" ? new TextEncoder().encode(body) : body,
          ContentType: opts.contentType ?? "application/json",
        }),
      );
    },
    async remove(rawKey) {
      const s3 = await cfg.getClient();
      await s3.send(new DeleteObjectCommand({ Bucket: cfg.bucket, Key: rawKey }));
    },
    analyze(entry, bytes) {
      return analyzeAccess(entry, bytes, cfg.slug);
    },
  };
}
